import api from 'api/config';
import { extractValue } from 'utils/sqlHelpers';

/**
 * Profile Service for managing user profiles
 * Provides methods to interact with the profile API endpoints
 */
export const profileService = {
  // ========== PROFILE MANAGEMENT ==========

  /**
   * Get user profile
   * @param {Object} params - Query parameters
   * @param {number} params.userId - Specific user ID to get profile for (optional, defaults to authenticated user)
   * @returns {Promise} Response with profile data
   */
  getProfile: async (params = {}) => {
    try {
      const queryParams = {};
      if (params.userId) {
        queryParams.user_id = params.userId;
      }

      const response = await api.get('/profile', { params: queryParams });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Failed to get profile:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve profile',
      };
    }
  },

  /**
   * Create a new user profile (admin only)
   * @param {Object} profileData - Profile creation data
   * @param {number} profileData.userId - User ID for the profile
   * @param {string} profileData.firstName - User's first name (optional)
   * @param {string} profileData.lastName - User's last name (optional)
   * @param {string} profileData.bio - User's biography (optional)
   * @param {string} profileData.avatarUrl - User's avatar URL (optional)
   * @returns {Promise} Response with success message
   */
  createProfile: async (profileData) => {
    try {
      const payload = {
        user_id: profileData.userId,
        ...(profileData.firstName && { first_name: profileData.firstName }),
        ...(profileData.lastName && { last_name: profileData.lastName }),
        ...(profileData.bio && { bio: profileData.bio }),
        ...(profileData.avatarUrl && { avatar_url: profileData.avatarUrl }),
      };

      const response = await api.post('/profile', payload);
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to create profile:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to create profile',
      };
    }
  },

  /**
   * Update user profile
   * @param {Object} updates - Profile fields to update
   * @param {number} updates.userId - User ID (admin only, optional)
   * @param {string} updates.firstName - Updated first name (optional)
   * @param {string} updates.lastName - Updated last name (optional)
   * @param {string} updates.bio - Updated biography (optional)
   * @param {string} updates.avatarUrl - Updated avatar URL (optional)
   * @returns {Promise} Response with success message
   */
  updateProfile: async (updates) => {
    try {
      const payload = {};

      if (updates.userId !== undefined) payload.user_id = updates.userId;
      if (updates.firstName !== undefined)
        payload.first_name = updates.firstName;
      if (updates.lastName !== undefined) payload.last_name = updates.lastName;
      if (updates.bio !== undefined) payload.bio = updates.bio;
      if (updates.avatarUrl !== undefined)
        payload.avatar_url = updates.avatarUrl;
      if (updates.location !== undefined) payload.location = updates.location;
      if (updates.website !== undefined) payload.website = updates.website;
      if (updates.linkedinUrl !== undefined)
        payload.linkedin_url = updates.linkedinUrl;
      if (updates.company !== undefined) payload.company = updates.company;
      if (updates.position !== undefined) payload.position = updates.position;
      if (updates.jobTitle !== undefined) payload.job_title = updates.jobTitle;
      if (updates.experienceLevel !== undefined)
        payload.experience_level = updates.experienceLevel;
      if (updates.hourlyRate !== undefined)
        payload.hourly_rate = parseFloat(updates.hourlyRate) || null;
      if (updates.skills !== undefined) payload.skills = updates.skills;

      const response = await api.put('/profile', payload);
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to update profile:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update profile',
      };
    }
  },

  // ========== EXPERIENCE MANAGEMENT ==========

  updateExperience: async (experience) => {
    try {
      const response = await api.post('/profile/experience', {
        experience: experience || [],
      });
      return {
        success: true,
        message: response.data.message,
        experience: response.data.experience,
      };
    } catch (error) {
      console.error('Failed to update experience:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update experience',
      };
    }
  },

  // ========== EDUCATION MANAGEMENT ==========

  updateEducation: async (education) => {
    try {
      const response = await api.post('/profile/education', {
        education: education || [],
      });
      return {
        success: true,
        message: response.data.message,
        education: response.data.education,
      };
    } catch (error) {
      console.error('Failed to update education:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update education',
      };
    }
  },

  // ========== USER CONTRACTS ==========

  /**
   * Get contracts for a user's profile
   * Returns contracts where the user is either the assigned worker or the business owner
   * @param {Object} params - Query parameters
   * @param {number} params.userId - Specific user ID to get contracts for (optional, defaults to authenticated user)
   * @param {string} params.status - Filter by contract status (optional)
   * @param {number} params.page - Page number for pagination (optional, default: 1)
   * @param {number} params.pageSize - Number of items per page (optional, default: 10, max: 50)
   * @returns {Promise} Response with contracts data and pagination info
   */
  getUserContracts: async (params = {}) => {
    try {
      const queryParams = {};
      if (params.userId) {
        queryParams.user_id = params.userId;
      }
      if (params.status) {
        queryParams.status = params.status;
      }
      if (params.page) {
        queryParams.page = params.page;
      }
      if (params.pageSize) {
        queryParams.page_size = params.pageSize;
      }

      const response = await api.get('/profile/contracts', {
        params: queryParams,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Failed to get user contracts:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve contracts',
      };
    }
  },

  /**
   * Delete a contract owned by the authenticated user
   * NOTE: Backend enforces ownership and status constraints.
   * @param {string} publicId - Contract public ID
   * @returns {Promise} Response with success flag and message/error
   */
  deleteUserContract: async (publicId) => {
    try {
      if (!publicId) {
        throw new Error('Contract public ID is required');
      }

      const response = await api.delete(`/contracts/${publicId}`);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Failed to delete user contract:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete contract',
      };
    }
  },

  // ========== WORK HISTORY MANAGEMENT ==========

  /**
   * Get work history for a user
   * @param {Object} params - Query parameters
   * @param {number} params.userId - Specific user ID to get work history for (optional, defaults to authenticated user)
   * @returns {Promise} Response with work history data including projects and summary
   */
  getWorkHistory: async (params = {}) => {
    try {
      const queryParams = {};
      if (params.userId) {
        queryParams.user_id = params.userId;
      }

      const response = await api.get('/profile/work-history', {
        params: queryParams,
      });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Failed to get work history:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve work history',
      };
    }
  },

  /**
   * Submit a review for a completed contract
   * @param {string} contractId - The public ID of the contract to review
   * @param {Object} reviewData - Review data
   * @param {number} reviewData.rating - Rating from 1-5
   * @param {string} reviewData.review - Review text (optional)
   * @returns {Promise} Response with success status
   */
  submitWorkHistoryReview: async (contractId, { rating, review }) => {
    try {
      const response = await api.post(
        `/profile/work-history/${contractId}/review`,
        {
          rating,
          review_text: review || '',
        }
      );
      return {
        success: true,
        message: response.data.message,
      };
    } catch (error) {
      console.error('Failed to submit work history review:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to submit review',
      };
    }
  },

  // ========== USER SETTINGS (PROFILE.SETTINGS) ==========

  /**
   * Get user settings
   * @returns {Promise} Response with settings data
   */
  getUserSettings: async () => {
    try {
      const response = await api.get('/user/settings');
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Failed to get user settings:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to retrieve settings',
      };
    }
  },

  /**
   * Update user settings
   * @param {Object} settings - Settings to update
   * @returns {Promise} Response with success status
   */
  updateUserSettings: async (settings) => {
    try {
      const response = await api.put('/user/settings', settings);
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      console.error('Failed to update user settings:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to update settings',
      };
    }
  },
};

/**
 * Utility functions for profiles
 */
export const profileUtils = {
  /**
   * Format profile data for display
   * @param {Object} profile - Raw profile data from API
   * @returns {Object} Formatted profile data
   */
  formatProfile: (profile) => {
    if (!profile) return null;

    // Extract actual string values from sql.NullString structures
    const firstName = extractValue(profile.first_name, '');
    const lastName = extractValue(profile.last_name, '');
    const bio = extractValue(profile.bio, '');
    const avatarUrl = extractValue(profile.avatar_url, '');
    const location = extractValue(profile.location, '');
    const website = extractValue(profile.website, '');
    const linkedinUrl = extractValue(profile.linkedin_url, '');
    const company = extractValue(profile.company, '');
    const position = extractValue(profile.position, '');
    const jobTitle = extractValue(profile.job_title, '');
    const experienceLevel = extractValue(profile.experience_level, '');
    const skills = extractValue(profile.skills, '');
    const hourlyRate = extractValue(profile.hourly_rate);

    return {
      ...profile,
      first_name: firstName,
      last_name: lastName,
      bio: bio,
      avatar_url: avatarUrl,
      location: location,
      website: website,
      linkedin_url: linkedinUrl,
      company: company,
      position: position,
      job_title: jobTitle,
      experience_level: experienceLevel,
      hourly_rate: hourlyRate,
      skills: skills,
      fullName: profileUtils.getFullName(firstName, lastName),
      displayName: profileUtils.getDisplayName(firstName, lastName),
      initials: profileUtils.getInitials(firstName, lastName),
      created_at: profile.created_at,
      updated_at: profile.updated_at,
      createdAt: profile.created_at ? new Date(profile.created_at) : null,
      updatedAt: profile.updated_at ? new Date(profile.updated_at) : null,
    };
  },

  /**
   * Get full name from first and last name
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   * @returns {string} Full name or fallback
   */
  getFullName: (firstName, lastName) => {
    const first = firstName?.trim() || '';
    const last = lastName?.trim() || '';

    if (first && last) {
      return `${first} ${last}`;
    } else if (first) {
      return first;
    } else if (last) {
      return last;
    }

    return 'Anonymous User';
  },

  /**
   * Get display name (first name or full name)
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   * @returns {string} Display name
   */
  getDisplayName: (firstName, lastName) => {
    const first = firstName?.trim() || '';

    if (first) {
      return first;
    }

    return profileUtils.getFullName(firstName, lastName);
  },

  /**
   * Get initials from first and last name
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   * @returns {string} Initials (up to 2 characters)
   */
  getInitials: (firstName, lastName) => {
    const first = firstName?.trim() || '';
    const last = lastName?.trim() || '';

    let initials = '';

    if (first) {
      initials += first.charAt(0).toUpperCase();
    }

    if (last && initials.length < 2) {
      initials += last.charAt(0).toUpperCase();
    }

    return initials || 'AU'; // Anonymous User
  },

  /**
   * Generate a simple hash from a string for deterministic color selection
   * @param {string} str - Input string to hash
   * @returns {number} Hash number (0-2147483647)
   */
  getStringHash: (str) => {
    let hash = 0;
    if (str.length === 0) return hash;

    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }

    return Math.abs(hash); // Ensure positive number
  },

  /**
   * Validate profile data
   * @param {Object} profileData - Profile data to validate
   * @returns {Object} Validation result with isValid and errors
   */
  validateProfileData: (profileData) => {
    const errors = {};

    // First name validation
    if (profileData.firstName && profileData.firstName.length > 50) {
      errors.firstName = 'First name must be 50 characters or less';
    }

    // Last name validation
    if (profileData.lastName && profileData.lastName.length > 50) {
      errors.lastName = 'Last name must be 50 characters or less';
    }

    // Bio validation
    if (profileData.bio && profileData.bio.length > 1000) {
      errors.bio = 'Bio must be 1000 characters or less';
    }

    // Avatar URL validation
    if (
      profileData.avatarUrl &&
      !profileUtils.isValidUrl(profileData.avatarUrl)
    ) {
      errors.avatarUrl = 'Avatar URL must be a valid URL';
    }

    // User ID validation (for admin operations)
    if (
      profileData.userId !== undefined &&
      (!Number.isInteger(profileData.userId) || profileData.userId <= 0)
    ) {
      errors.userId = 'User ID must be a positive integer';
    }
    if (profileData.skills) {
      const skillsArray = Array.isArray(profileData.skills)
        ? profileData.skills
        : profileData.skills.split(',').filter((s) => s.trim());

      if (skillsArray.length < 3) {
        errors.skills = 'At least 3 skills are required';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors,
    };
  },

  /**
   * Validate URL format
   * @param {string} url - URL to validate
   * @returns {boolean} Whether URL is valid
   */
  isValidUrl: (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  },

  /**
   * Generate avatar placeholder URL
   * @param {string} firstName - First name
   * @param {string} lastName - Last name
   * @param {Object} options - Avatar options
   * @param {number} options.size - Avatar size in pixels (default: 200)
   * @param {string} options.background - Background color (default: random)
   * @returns {string} Avatar placeholder URL
   */
  generateAvatarPlaceholder: (firstName, lastName, options = {}) => {
    const initials = profileUtils.getInitials(firstName, lastName);
    const size = options.size || 200;
    // Gmail-inspired color palette for profile avatars
    // These colors are designed to be professional and work well with white text
    const colors = [
      'EA4335', //  Red
      '4285F4', //  Blue
      '34A853', //  Green
      'FBBC04', //  Yellow
      '9333EA', // Purple
      '06B6D4', // Cyan
      'F97316', // Orange
      'EC4899', // Pink
      '8B5CF6', // Violet
      '14B8A6', // Teal
      'F59E0B', // Amber
      'EF4444', // Dark Red
    ];

    // Generate deterministic background color based on user's name
    // This ensures the same user always gets the same avatar background color
    const background =
      options.background ||
      colors[profileUtils.getStringHash(firstName + lastName) % colors.length];

    return `https://ui-avatars.com/api/?name=${encodeURIComponent(
      initials
    )}&size=${size}&background=${background}&color=fff&bold=true`;
  },

  /**
   * Check if profile is complete
   * @param {Object} profile - Profile data
   * @returns {Object} Completion status with percentage and missing fields
   */
  getProfileCompleteness: (profile) => {
    if (!profile) {
      return {
        percentage: 0,
        missingFields: ['first_name', 'last_name', 'bio', 'avatar_url'],
      };
    }

    const fields = ['first_name', 'last_name', 'bio', 'avatar_url'];
    const completedFields = fields.filter((field) => {
      const value = profile[field];
      return value && value.trim && value.trim().length > 0;
    });

    const percentage = Math.round(
      (completedFields.length / fields.length) * 100
    );
    const missingFields = fields.filter(
      (field) => !completedFields.includes(field)
    );

    return {
      percentage,
      missingFields,
      completedFields,
    };
  },

  /**
   * Format profile for API submission
   * @param {Object} formData - Form data from UI
   * @returns {Object} Formatted data for API
   */
  formatForSubmission: (formData) => {
    const formatted = {};

    if (formData.firstName !== undefined && formData.firstName !== null) {
      formatted.firstName = formData.firstName.toString().trim();
    }
    if (formData.lastName !== undefined && formData.lastName !== null) {
      formatted.lastName = formData.lastName.toString().trim();
    }
    if (formData.bio !== undefined && formData.bio !== null) {
      formatted.bio = formData.bio.toString().trim();
    }
    if (formData.avatarUrl !== undefined) {
      if (formData.avatarUrl === null || formData.avatarUrl === '') {
        formatted.avatarUrl = null;
      } else {
        formatted.avatarUrl = formData.avatarUrl.toString().trim();
      }
    }
    if (formData.location !== undefined && formData.location !== null) {
      formatted.location = formData.location.toString().trim();
    }
    if (formData.website !== undefined && formData.website !== null) {
      formatted.website = formData.website.toString().trim();
    }
    if (formData.linkedinUrl !== undefined && formData.linkedinUrl !== null) {
      formatted.linkedinUrl = formData.linkedinUrl.toString().trim();
    }
    if (formData.company !== undefined && formData.company !== null) {
      formatted.company = formData.company.toString().trim();
    }
    if (formData.position !== undefined && formData.position !== null) {
      formatted.position = formData.position.toString().trim();
    }
    if (formData.jobTitle !== undefined && formData.jobTitle !== null) {
      formatted.jobTitle = formData.jobTitle.toString().trim();
    }
    if (
      formData.experienceLevel !== undefined &&
      formData.experienceLevel !== null
    ) {
      formatted.experienceLevel = formData.experienceLevel.toString().trim();
    }
    if (
      formData.hourlyRate !== undefined &&
      formData.hourlyRate !== null &&
      formData.hourlyRate !== ''
    ) {
      formatted.hourlyRate = parseFloat(formData.hourlyRate) || null;
    }
    if (formData.skills !== undefined && formData.skills !== null) {
      formatted.skills = formData.skills.toString().trim();
    }
    if (formData.userId !== undefined && formData.userId !== null) {
      formatted.userId = parseInt(formData.userId, 10);
    }

    return formatted;
  },

  /**
   * Get time ago string for profile timestamps
   * @param {Date|string} timestamp - Timestamp to format
   * @returns {string} Time ago string
   */
  getTimeAgo: (timestamp) => {
    if (!timestamp) return '';

    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);
    const diffMonths = Math.floor(diffDays / 30);
    const diffYears = Math.floor(diffDays / 365);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 30) return `${diffDays}d ago`;
    if (diffMonths < 12) return `${diffMonths}mo ago`;

    return `${diffYears}y ago`;
  },
};

export default profileService;
